import { Space } from "antd";
import { FC, useEffect, useState } from "react";
import { useLoading } from "../../context/LoadingProvider/useLoading";
import { ActionButtonEnum } from "../../enums/actionButtonEnum";
import { SearchOptionEnum } from "../../enums/searchOptionEnum";
import useActionButtons from "../../hooks/useActionButtons";
import useSmartSearchParams, {
  SmartSearchParams,
} from "../../hooks/useSmartSearchParams";
import { Output } from "../../services/types";
import { isString } from "../../utils/stringUtils";
import Button from "../UI/Button";
import RangePicker, {
  RangePickerValue,
  RangePickerValues,
} from "../UI/RangePicker";
import SmartSearch from "../UI/SmartSearch";
import DefaultFormParserLayout from "./default-form-parser-layout";
import { ISelectInputProps, ISelectOption } from "../UI/SelectInput/types";
import SelectInput from "../UI/SelectInput";
import { ValidationEnum } from "../../enums/validationEnum";
import dayjs from "dayjs";

type SelectInputProps = Omit<ISelectInputProps, "onSelectChange" | "value">;

export type SmartSearchDateIntervalSelectAndActionsLayoutProps = {
  searchBy: SearchOptionEnum[];
  validOnEmpty?: boolean;
  actions: ActionButtonEnum[];
  selectProps?: SelectInputProps;
  validations?: ValidationEnum[];
  onRefresh: (
    output: Output,
    params: SmartSearchParams,
    startDateInterval: RangePickerValue,
    endDateInterval: RangePickerValue,
    select?: ISelectOption["value"]
  ) => Promise<string | void>;
};

const SmartSearchDateIntervalSelectAndActionsLayout: FC<
  SmartSearchDateIntervalSelectAndActionsLayoutProps
> = ({
  searchBy,
  actions,
  validOnEmpty,
  validations,
  selectProps,
  onRefresh,
}) => {
  const TODAY = dayjs();
  const LAST_WEEK = dayjs().subtract(1, "week");

  const loading = useLoading();
  const [params, setParams] = useSmartSearchParams();
  const [search, setSearch] = useState("");
  const [validSearch, setValidSearch] = useState(false);
  const [report, setReport] = useState("");
  const [select, setSelect] = useState<ISelectOption["value"]>(
    selectProps?.options[0].value || ""
  );

  const {
    isToShowRefreshAction,
    isToShowSaveCSVAction,
    isToShowSavePDFAction,
  } = useActionButtons(actions);

  const [timeInterval, setTimeInterval] = useState<RangePickerValues>([
    LAST_WEEK,
    TODAY,
  ]);

  const isToValidateSmartSearch =
    validations?.includes(ValidationEnum.VALIDATE_SMART_SEARCH) && !validSearch;

  const isToValidateSearchInput =
    validations?.includes(ValidationEnum.VALIDATE_SEARCH) && !search.length;

  const disabled =
    isToValidateSmartSearch ||
    isToValidateSearchInput ||
    (search.length > 0 && !validSearch);

  const refreshReport = async (output: Output = Output.HTML) => {
    loading.addLoading();

    const lastWeek = timeInterval[0];
    const today = timeInterval[1];

    const res = await onRefresh(output, params, lastWeek, today, select);

    if (Output.isHTML(output) && isString(res)) {
      setReport(res);
    }

    loading.removeLoading();
  };

  return (
    <DefaultFormParserLayout html={report}>
      <Space direction="horizontal" size="small" wrap>
        <SmartSearch
          autoFocus
          validOnEmpty={validOnEmpty}
          label="Smart Search"
          status="active,closed"
          searchBy={searchBy}
          onChange={(e: string) => setSearch(e)}
          value={search}
          onValidSearch={setValidSearch}
          onObjValueChange={(e: any) => setParams({ ...params, ...e })}
        />

        {selectProps && (
          <SelectInput
            {...selectProps}
            value={select}
            onSelectChange={(e) => {setSelect(e?.value ? e.value : e)}}
          />
        )}

        <RangePicker
          defaultValue={[LAST_WEEK, TODAY]}
          onChange={(e) => setTimeInterval(e)}
          allowClear={false}
          format="MM-DD-YYYY"
        />

        {isToShowRefreshAction && (
          <Button
            type="primary"
            disabled={disabled}
            onClick={() => refreshReport()}
          >
            Refresh
          </Button>
        )}

        {isToShowSaveCSVAction && (
          <Button
            type="primary"
            disabled={disabled}
            onClick={() => refreshReport(Output.CSV)}
          >
            Save as CSV
          </Button>
        )}

        {isToShowSavePDFAction && (
          <Button
            type="primary"
            disabled={disabled}
            onClick={() => refreshReport(Output.PDF)}
          >
            Save as PDF
          </Button>
        )}
      </Space>
    </DefaultFormParserLayout>
  );
};

export default SmartSearchDateIntervalSelectAndActionsLayout;
